import { Mail } from '@mui/icons-material';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
} from '@mui/material';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { sendMail } from 'lib/api/mailing';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { isValidEmail, showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { SyntheticEvent } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  warranty: Warranty;
};

const MailDialog: React.FC<Props> = ({ open, setOpen, warranty }) => {
  const dispatch = useDispatch();
  const identifier = warranty.businessIdentifier;
  const [mail, setMail] = useState<string | null>(null);
  const query = new URLSearchParams({
    isLegalRepresentative: 'true',
  }).toString();
  const { data } = getBusinessContacts(dispatch, identifier, query);
  const { mutate: sendMailMutation, isPending: isSendingMail } = sendMail(
    dispatch,
    warranty.id,
  );

  const contacts = data?.items ?? [];

  const getOptionLabel = (option: string | Contact) => {
    if (typeof option === 'string') {
      return option;
    }
    return `${option.name} <${option.email}>`;
  };

  const getOptionKey = (option: string | Contact) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.id;
  };

  const handleMailChange = (
    event: SyntheticEvent<Element, Event>,
    value: string | Contact | null,
  ) => {
    if (typeof value === 'string') {
      setMail(value);
    } else {
      setMail(value?.email ?? null);
    }
  };

  const handleSend = async () => {
    if (!mail || !isValidEmail(mail)) {
      showAlert(dispatch, 'error', 'Por favor selecciona un mail');
      return;
    }
    sendMailMutation(
      { recipients: [mail] },
      {
        onSuccess: () => {
          showAlert(dispatch, 'success', 'Correo enviado correctamente');
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Seleccionar mail"
        icon={<Mail color="primary" />}
        color="primary"
      />
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <Autocomplete
            sx={{ margin: 'dense' }}
            freeSolo
            options={contacts ?? []}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderInput={(params) => <TextField {...params} label="Mail" />}
            onChange={handleMailChange}
            value={mail}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={handleSend}
          loading={isSendingMail}
          disabled={isSendingMail}
        >
          Enviar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default MailDialog;
